import React, { useState, useEffect } from 'react';
import { ModalBody } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical, faTimes, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { Button, Popover } from 'antd';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Input, FormGroup } from 'reactstrap';
import { faEye } from '@fortawesome/free-regular-svg-icons';
import './GetDealColumnsPopover.css';

const listStyle = { minHeight: '100px', padding: '4px' };
const itemStyle = (isDragging) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: isDragging ? '#e6f7ff' : '#f5f5f5',
  padding: '6px',
  marginBottom: '.5em',
  borderRadius: '4px',
});

export default function GetDealColumnsPopover({ fields, selected, setSelected }) {
  const [searchValue, setSearchValue] = useState('');
  const [open, setOpen] = useState(false);

  // Active header data with isChecked
  const [activeHeaderData, setActiveHeaderData] = useState(
    fields.map(f => ({
      name: typeof f === 'string' ? f : f.name,
      field: typeof f === 'string' ? f.replace(/\s/g, '_') : f.name.replace(/\s/g, '_'),
      isChecked: typeof f === 'string' && f === 'Deal Name' ? false : true,
      isSearch: true,
    }))
  );

  // Sync with parent selected
useEffect(() => {
  if (!fields || fields.length === 0) return;

  const headers = fields.map(f => {
    const name = typeof f === 'string' ? f : f.name;
    return {
      name,
      field: name.replace(/\s/g, '_'),
      isChecked: name === 'Deal Name' ? false : !(selected?.[name] ?? true),
      isSearch: true
    };
  });
  setActiveHeaderData(headers);
}, [fields, selected]);

  // Toggle individual column
const handleToggleColumn = (index) => {
  const headers = [...activeHeaderData];
  if (headers[index].field === 'Deal_Name') return; 
  headers[index].isChecked = !headers[index].isChecked;

  const updatedSelected = {};
  headers.forEach(h => {
    updatedSelected[h.name] = !h.isChecked;
  });

  setSelected(updatedSelected); 
};
const handleSelectAll = () => {
  const headers = activeHeaderData.map(h => ({
    ...h,
    isChecked: h.field === 'Deal_Name' ? false : false
  }));
  setActiveHeaderData(headers);

  const updated = {};
  headers.forEach(h => updated[h.name] = !h.isChecked);
  setSelected(updated);
};

  // Reset all
const handleDeselectAll = () => {
  const headers = activeHeaderData.map(h => ({
    ...h,
    isChecked: h.field === 'Deal_Name' ? false : true
  }));
  setActiveHeaderData(headers);

  const updated = {};
  headers.forEach(h => updated[h.name] = !h.isChecked);
  setSelected(updated);
};

  // Update parent selected state
  const updateParent = (headers) => {
    const updated = {};
    headers.forEach(h => updated[h.name] = !h.isChecked);
    setSelected(updated);
  };

  // Drag & drop handler
  const onDragEnd = (result) => {
    if (!result.destination) return;
    const visibleColumns = activeHeaderData.filter(col => !col.isChecked);
    const [moved] = visibleColumns.splice(result.source.index, 1);
    visibleColumns.splice(result.destination.index, 0, moved);

    const hiddenColumns = activeHeaderData.filter(col => col.isChecked);
    setActiveHeaderData([...visibleColumns, ...hiddenColumns]);
    updateParent([...visibleColumns, ...hiddenColumns]);
  };

  // Handle select from available fields
  const handleSelect = (name) => {
    const index = activeHeaderData.findIndex(h => h.name === name);
    if (index !== -1) handleToggleColumn(index);
  };

  const content = () => (
    <ModalBody style={{ minWidth: '550px' }}>
      <FormGroup style={{ marginBottom: '14px', padding:'5px' }}>
        <Input
          type="text"
          placeholder="Search available fields"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />
      </FormGroup>

      <div style={{ display: 'flex', gap: '32px' }}>
        {/* Available Fields */}
        <div style={{ 
              flex: 1, 
              border: '1px solid #ddd', 
              borderRadius: '4px', 
              padding: '12px', 
              maxHeight: '420px', 
              overflowY: 'auto' 
            }}>
          <div style={{display:'flex', justifyContent:'space-between', marginBottom:'3px'}}>
            <h6 style={{ fontWeight: 600 }}>Available Fields</h6>
            <Button shape='circle'>{activeHeaderData.filter(h => h.isChecked).length}</Button>
          </div>
          <ul style={{ listStyle: 'none', padding: 0, maxHeight: '325px', overflowY: 'auto' }}>
            {activeHeaderData
              .filter(item => item.name.toLowerCase().includes(searchValue.toLowerCase()))
              .map(item => (
                <li
                  key={item.field}
                  className="toSelectItems"
                  style={{
                    background: !item.isChecked ? '#f5f5f5' : '#f0efef',
                    pointerEvents: item.name === 'Deal Name' ? 'none' : 'auto',
                    opacity: item.name === 'Deal Name' ? 0.5 : 1,
                  }}
                  onClick={() => handleSelect(item.name)}
                >
                  <span>{item.name}</span>
                  {item.isChecked && <FontAwesomeIcon icon={faArrowRight} />}
                  {item.name === 'Deal Name' && <small>Primary Field</small>}
                </li>
              ))}
          </ul>
        <Button size="small" onClick={handleSelectAll}>Select All</Button>
        </div>

        {/* Selected Fields */}
       <div style={{ 
    flex: 1,
    border: '1px solid #ddd',
    borderRadius: '4px',
    display: 'flex',
    flexDirection: 'column', // column layout
    height: '420px',         // fixed height
  }}>
  <div
    style={{display: 'flex',justifyContent: 'space-between',alignItems: 'center',padding: '12px 12px 6px 12px',flexShrink: 0, // header fixed
}}>
    <h6 style={{ fontWeight: 600, margin: 0 }}>Selected Fields</h6>
    <div
      style={{
        color: '#fff',
        borderRadius: '50%',
        padding: '0 8px',
        fontSize: '0.9rem',
      }}
    >
      <Button shape='circle'>{activeHeaderData.filter(h => !h.isChecked).length}</Button>
    </div>
  </div>

  {/* Scrollable list */}
  <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px' }}>
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="selected-columns">
        {(provided) => (
          <ul
            {...provided.droppableProps}
            ref={provided.innerRef}
            style={{ listStyle: 'none', padding: 0, margin: 0 }}
          >
                  {activeHeaderData.filter(col => !col.isChecked).map((col, index) => (
                <Draggable key={col.field} draggableId={col.field} index={index}>
                  {(provided, snapshot) => (
                    <li
                      ref={provided.innerRef} 
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className="selectedFieldItem"
                      style={{
                        ...itemStyle(snapshot.isDragging),
                        ...provided.draggableProps.style,
                        display: 'flex',
                        justifyContent: 'space-between', // text left, icons right
                        alignItems: 'center',
                        pointerEvents: col.field === 'Deal_Name' ? 'none' : 'auto',
                        opacity: col.field === 'Deal_Name' ? 0.6 : 1,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FontAwesomeIcon icon={faGripVertical} />
                        <span>{col.name}</span>
                        {col.field === 'Deal_Name' && <small>Primary Field</small>}
                      </div>
                        <FontAwesomeIcon
                          icon={faTimes}
                          onClick={() => handleToggleColumn(activeHeaderData.indexOf(col))}
                          style={{ cursor: 'pointer', color: '#e04343' }}
                        />
                    </li>
                  )}
                </Draggable>
              ))}
            {provided.placeholder}
          </ul>
        )}
      </Droppable>
    </DragDropContext>
  </div>
  <div
    style={{
      padding: '8px 12px',
      // borderTop: '1px solid #ddd',
      flexShrink: 0, // footer fixed
    }}
  >
    <Button size="small" onClick={handleDeselectAll}>Reset All </Button>
  </div>
</div>
      </div>
    </ModalBody>
  );

  return (
    <Popover
      content={content}
      rootClassName="custom-popover-tasks-manage-columns"
      title="Manage Columns"
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomLeft"
    >
      <Button type="default">
        <FontAwesomeIcon color='#95c4ff' icon={faEye}/> 
        Fields to Display 
        <div className='count-badge'>{activeHeaderData.filter(h => !h.isChecked).length}</div>
      </Button>
    </Popover>
  );
}